import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import { apiFetch } from '../../lib/api';
import { MarkdownMinimo } from '../../utils/markdownMinimo';
import { API_DOCS } from './KnowledgeBasePage.api';

interface KbDocumentoRiga {
  slug: string;
  titolo: string;
  riassunto: string;
  moduli: string[];
  aggiornato: string | null;
  attivo: boolean;
  generato: boolean;
  syncAt: string;
}
interface KbDocumentoDettaglio extends KbDocumentoRiga { contenuto: string; }

function fmtData(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('it-IT') : '—';
}

export default function KnowledgeBasePage() {
  const [documenti, setDocumenti] = useState<KbDocumentoRiga[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ricerca, setRicerca] = useState('');
  const [slugAttivo, setSlugAttivo] = useState<string | null>(null);
  const [dettaglio, setDettaglio] = useState<KbDocumentoDettaglio | null>(null);
  const [loadingDettaglio, setLoadingDettaglio] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true); setError('');
      try {
        // @api GET /api/assistenza/kb -> { success: true, data: [{ slug, titolo, riassunto, moduli, aggiornato, attivo, generato, syncAt }] }
        const res = await apiFetch<{ success: boolean; data: KbDocumentoRiga[] }>('/api/assistenza/kb');
        const lista = res.data ?? [];
        setDocumenti(lista);
        // ?doc=<slug> arriva dai link "fonti" della chat con l'assistente
        const richiesto = new URLSearchParams(window.location.search).get('doc');
        const scelto = (richiesto && lista.find(d => d.slug === richiesto))
          || lista.find(d => !d.generato) || lista[0];
        if (scelto) setSlugAttivo(scelto.slug);
      } catch (e: any) {
        setError(e.message || 'Errore nel caricamento');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!slugAttivo) { setDettaglio(null); return; }
    (async () => {
      setLoadingDettaglio(true);
      try {
        // @api GET /api/assistenza/kb/:slug -> { success: true, data: { slug, titolo, riassunto, moduli, aggiornato, attivo, generato, syncAt, contenuto } }
        const res = await apiFetch<{ success: boolean; data: KbDocumentoDettaglio }>(`/api/assistenza/kb/${slugAttivo}`);
        setDettaglio(res.data);
      } catch (e: any) {
        setError(e.message || 'Errore nel caricamento del documento');
      } finally {
        setLoadingDettaglio(false);
      }
    })();
  }, [slugAttivo]);

  const filtrati = useMemo(() => {
    const q = ricerca.trim().toLowerCase();
    if (!q) return documenti;
    return documenti.filter(d =>
      d.titolo.toLowerCase().includes(q) || d.riassunto.toLowerCase().includes(q) || d.moduli.some(m => m.includes(q)));
  }, [documenti, ricerca]);

  return (
    <Layout>
      <div className="bop-page-header">
        <div className="bop-page-header__text">
          <h2>Knowledge base</h2>
          <p>I documenti che l'assistente legge prima di aprire un ticket. Si aggiornano a ogni rilascio.</p>
        </div>
      </div>

      {error && <div className="bop-inline-error">{error}</div>}

      <div className="bop-kb">
        <div className="bop-kb__colonna">
          <div className="bop-card">
            <div className="bop-card__header"><h3>Documenti ({documenti.length})</h3></div>
            <div className="bop-card__body">
              <input
                className="bop-input"
                placeholder="Cerca per titolo, argomento o modulo…"
                value={ricerca}
                onChange={e => setRicerca(e.target.value)}
              />
              {loading ? (
                <div className="bop-empty-state">Caricamento…</div>
              ) : filtrati.length === 0 ? (
                <div className="bop-empty-state">Nessun documento.</div>
              ) : (
                <ul className="bop-kb__lista">
                  {filtrati.map(d => (
                    <li
                      key={d.slug}
                      className={`bop-kb__voce${d.slug === slugAttivo ? ' bop-kb__voce--selezionata' : ''}`}
                      onClick={() => setSlugAttivo(d.slug)}
                    >
                      <div className="bop-kb__voce-titolo">
                        {d.titolo}
                        {d.generato && <span className="bop-badge bop-badge--neutral">generato</span>}
                      </div>
                      <div className="bop-kb__voce-riassunto">{d.riassunto}</div>
                      <div className="bop-kb__voce-meta">
                        {d.moduli.length > 0 ? d.moduli.join(', ') : 'tutti i moduli'} · agg. {fmtData(d.aggiornato)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className="bop-kb__colonna">
          <div className="bop-card">
            <div className="bop-card__header">
              <h3>{dettaglio?.titolo ?? 'Documento'}</h3>
              {dettaglio && (
                <span className="bop-text-muted bop-kb__slug">{dettaglio.slug} · caricato il {fmtData(dettaglio.syncAt)}</span>
              )}
            </div>
            <div className="bop-card__body">
              {loadingDettaglio ? (
                <div className="bop-empty-state">Caricamento…</div>
              ) : dettaglio ? (
                <MarkdownMinimo testo={dettaglio.contenuto} />
              ) : (
                <div className="bop-empty-state">Seleziona un documento.</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
